"use client";

import { cn } from "@v1/ui/utils";
import { Loader2, Send } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect, useRef, useState } from "react";

/** Text composer: Enter sends on desktop, Shift+Enter breaks the line. */
export function MessengerComposer({
  onSend,
  disabled = false,
  autoFocus = false,
}: {
  /** Resolves true when the text was accepted (then the field clears). */
  readonly onSend: (text: string) => Promise<boolean>;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
}): ReactElement {
  const t = useTranslations("messenger.thread");
  const [text, setText] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const canSend = text.trim().length > 0 && !sending && !disabled;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [text]);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const send = async (): Promise<void> => {
    if (!canSend) return;
    setSending(true);
    try {
      if (await onSend(text.trim())) setText("");
    } finally {
      setSending(false);
      ref.current?.focus();
    }
  };

  return (
    <div className="shrink-0 border-t border-border/40 bg-background px-3 py-3">
      <div
        className={cn(
          "flex items-end gap-1 rounded-2xl border bg-background py-1.5 pl-3 pr-1.5 transition-colors",
          canSend ? "border-primary/25" : "border-border/60 focus-within:border-primary/25",
        )}
      >
        <textarea
          ref={ref}
          aria-label={t("platzhalter")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && window.matchMedia("(pointer: fine)").matches) {
              e.preventDefault();
              void send();
            }
          }}
          rows={1}
          disabled={disabled}
          placeholder={t("platzhalter")}
          className="max-h-[132px] min-h-[36px] min-w-0 flex-1 resize-none overflow-y-auto border-0 bg-transparent py-1.5 text-[16px] leading-relaxed text-primary/90 placeholder:text-primary/35 focus:outline-none disabled:opacity-60 md:text-[14px]"
        />
        <button
          type="button"
          disabled={!canSend}
          onClick={() => void send()}
          aria-label={t("senden")}
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full transition-all",
            canSend
              ? "bg-action-500 text-white shadow-sm hover:bg-action-600 active:scale-95"
              : "bg-action-500/[0.08] text-primary/35",
          )}
        >
          {sending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Send className="size-4" strokeWidth={2} aria-hidden />}
        </button>
      </div>
    </div>
  );
}
